import { pipe } from 'fp-ts/function';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { formValueSelector } from 'redux-form';
import { ComponentProps } from 'react';
import { FeatureFlagServiceProps, LocalizedText, lt, showSuccessAlert, withFeatureFlagService } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Stack } from '@optimus-web/core';
import { ApplicationState, CommanderThunkDispatch, Option, WithScope } from '@mews-commander/types';
import { CancellationReason } from '@mews-commander/legacy-data-structures';
import {
    CheckboxField,
    commanderForm,
    InputField,
    presence,
    SelectField,
    validation,
} from '@mews-commander/legacy-form';
import { FullWidthFieldsContainer } from '@mews-commander/ui';
import { RESERVATION_ORDER_ITEMS_EXTENT } from '../../../constants';
import { cancelReservation, fetchCurrentEnterpriseCheckedReservations, initializeStateTab } from '../../../actions';
import {
    APPLY_CANCELLATION_FEE,
    CANCELLATION_REASON,
    REASON_DETAILS,
    SEND_EMAIL_TO_CUSTOMER_AND_BOOKER,
} from './constants';
import { getCancellationFormInitialValues, getCancellationReasonOptions } from './selectors';

const cancellationFeeProps = {
    label: <LocalizedText textKey={TextKey.ApplyCancellationFee} />,
    name: APPLY_CANCELLATION_FEE,
};
const sendEmailToCustomerAndBookerProps = {
    label: <LocalizedText textKey={TextKey.SendEmailToCustomerAndBooker} />,
    name: SEND_EMAIL_TO_CUSTOMER_AND_BOOKER,
};
const reasonProps = {
    label: <LocalizedText textKey={TextKey.ReservationDetailReasonInputLabel} />,
    name: CANCELLATION_REASON,
    required: true,
};
const detailsProps = {
    label: <LocalizedText textKey={TextKey.DetailPlural} />,
    name: REASON_DETAILS,
};
const getConstraints = (
    _props: ReservationCancellationFormProps,
    { [CANCELLATION_REASON]: reason }: ReservationCancellationFormValues
) => ({
    [CANCELLATION_REASON]: { presence },
    [REASON_DETAILS]: reason === CancellationReason.Other.value ? { presence } : {},
});

interface ReservationCancellationFormValues {
    [APPLY_CANCELLATION_FEE]: boolean;
    [CANCELLATION_REASON]?: number;
    [REASON_DETAILS]?: string;
    [SEND_EMAIL_TO_CUSTOMER_AND_BOOKER]: boolean;
}

interface ReservationCancellationFormProps extends WithScope {
    areDetailsRequired: boolean;
    cancellationReasonOptions: Array<Option>;
}

const ReservationCancellationForm = ({
    areDetailsRequired,
    scope,
    cancellationReasonOptions,
}: ReservationCancellationFormProps) => (
    <FullWidthFieldsContainer>
        <SelectField {...reasonProps} options={cancellationReasonOptions} />
        <InputField {...detailsProps} required={areDetailsRequired} />
        <Stack distribution="stretch">
            <CheckboxField {...cancellationFeeProps} scope={scope} />
            <CheckboxField {...sendEmailToCustomerAndBookerProps} scope={scope} />
        </Stack>
    </FullWidthFieldsContainer>
);

const CommanderReservationCancellationForm = commanderForm<
    ReservationCancellationFormValues,
    ReservationCancellationFormProps
>({
    enableReinitialize: true,
    submitLabel: <LocalizedText textKey={TextKey.ReservationStatusCancellationButtonLabel} />,
    validate: validation(getConstraints),
})(ReservationCancellationForm);

type CommanderReservationCancellationFormProps = NonNullable<
    ComponentProps<typeof CommanderReservationCancellationForm>
>;
type StateProps = Pick<
    CommanderReservationCancellationFormProps,
    'cancellationReasonOptions' | 'areDetailsRequired' | 'initialValues'
>;
type DispatchProps = Pick<CommanderReservationCancellationFormProps, 'onSubmit'>;

interface OwnProps extends FeatureFlagServiceProps, WithScope {
    form: string;
    reservationIds: string[];
}

export default pipe(
    CommanderReservationCancellationForm,
    connect<StateProps, DispatchProps, OwnProps, ApplicationState>(
        createStructuredSelector({
            cancellationReasonOptions: getCancellationReasonOptions,
            initialValues: getCancellationFormInitialValues,
            areDetailsRequired: (state, { form }) =>
                formValueSelector(form)(state, CANCELLATION_REASON) === CancellationReason.Other.value,
        }),
        (dispatch: CommanderThunkDispatch, { reservationIds, scope }) => ({
            onSubmit: async ({
                cancellationReason,
                applyCancellationFee,
                reasonDetails,
                sendEmail,
            }: ReservationCancellationFormValues) =>
                dispatch(
                    cancelReservation({
                        reservationIds,
                        cancellationReason,
                        applyCancellationFee,
                        reasonDetails,
                        sendEmail,
                    })
                ).then(() => {
                    showSuccessAlert({ description: lt('ReservationCancelSuccessMessage') });
                    dispatch(
                        fetchCurrentEnterpriseCheckedReservations({ scope, extent: RESERVATION_ORDER_ITEMS_EXTENT })
                    );
                    void dispatch(initializeStateTab({ scope }));
                }),
        })
    ),
    withFeatureFlagService
);
